import { useEffect, useMemo, useState } from 'react';
import { api, formatMoney, mediaUrl } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { RefreshCcw, Package, DollarSign, TrendingUp, Clock, ShieldCheck, AlertTriangle, CheckCircle2, XCircle, MessageCircle, ExternalLink, ThumbsUp, ThumbsDown, Star, Truck, Percent, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { ShippingLabelModal } from '@/components/marketplace/ShippingModals';
import { OrderShipmentPanel } from '@/components/marketplace/OrderShipmentPanel';
import { useAuth } from '@/context/AuthContext';

const statusLabels = {
    pending_payment: { label: 'Pendiente pago', variant: 'outline', tone: 'text-amber-600' },
    paid: { label: 'Pagado (por enviar)', variant: 'secondary', tone: 'text-blue-600' },
    delivered: { label: 'Entregado', variant: 'secondary', tone: 'text-emerald-600' },
    released: { label: 'Completado', variant: 'default', tone: 'text-emerald-700' },
    refunded: { label: 'Reembolsado', variant: 'destructive', tone: 'text-red-600' },
    cancelled: { label: 'Cancelado', variant: 'destructive', tone: 'text-neutral-500' },
};

const PIE_COLORS = ['#dc2626', '#111827', '#f59e0b', '#3b82f6', '#10b981', '#a855f7'];

function StatCard({ icon: Icon, label, value, tone = 'text-red-600', testId }) {
    return (
        <div className="rounded-2xl border bg-card p-3 sm:p-4" data-testid={testId}>
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
                <Icon className="h-4 w-4" />{label}
            </div>
            <div className={`mt-1 text-lg sm:text-2xl font-bold ${tone}`}>{value}</div>
        </div>
    );
}

export default function SalesPage() {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const [filterStatus, setFilterStatus] = useState('all');
    const [search, setSearch] = useState('');

    const [rejectOrder, setRejectOrder] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    const [rateOrder, setRateOrder] = useState(null);
    const [stars, setStars] = useState(5);
    const [comment, setComment] = useState('');
    const [shipOrder, setShipOrder] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [o, s] = await Promise.all([
                api.get('/me/orders/sales'),
                api.get('/me/sales/stats'),
            ]);
            setOrders(o.data);
            setStats(s.data);
        } catch (e) {
            toast.error('Error cargando ventas');
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const currency = orders[0]?.currency?.toUpperCase() || 'MXN';

    const filtered = useMemo(() => {
        return orders.filter(o => {
            if (filterStatus === 'refund_pending') {
                return o.refund_request?.status === 'pending';
            }
            if (filterStatus !== 'all' && o.status !== filterStatus) return false;
            if (search) {
                const q = search.toLowerCase();
                const title = (o.ad_snapshot?.title || '').toLowerCase();
                const buyer = (o.buyer?.nickname || '').toLowerCase();
                if (!title.includes(q) && !buyer.includes(q)) return false;
            }
            return true;
        });
    }, [orders, filterStatus, search]);

    const markDelivered = async (id) => {
        try { await api.post(`/orders/${id}/mark-delivered`); toast.success('Marcado como entregado'); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const approveRefund = async (id) => {
        try {
            await api.post(`/orders/${id}/approve-refund`);
            toast.success('Reembolso aprobado y procesado en Stripe');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al aprobar reembolso'); }
    };

    const doRejectRefund = async () => {
        if (!rejectOrder) return;
        if (rejectReason.trim().length < 3) { toast.error('Ingresa un motivo (mín 3 caracteres)'); return; }
        try {
            await api.post(`/orders/${rejectOrder.id}/reject-refund`, { reason: rejectReason.trim() });
            toast.success('Reembolso rechazado');
            setRejectOrder(null); setRejectReason('');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const submitRating = async () => {
        try {
            await api.post('/ratings', { order_id: rateOrder.id, ratee_id: rateOrder.buyer_id, stars, comment });
            toast.success('Calificación enviada');
            setRateOrder(null); setStars(5); setComment('');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    // Commission derived stats
    const commissionStats = useMemo(() => {
        const completed = orders.filter(o => ['paid', 'delivered', 'released'].includes(o.status));
        const totalCommission = completed.reduce((s, o) => s + (o.commission_amount || 0), 0);
        const totalRevenue = completed.reduce((s, o) => s + (o.item_price || 0), 0);
        const byType = completed.reduce((acc, o) => {
            const t = o.ad_type || 'legacy';
            acc[t] = (acc[t] || 0) + 1;
            return acc;
        }, {});
        // daily commission series from orders
        const byDate = {};
        completed.forEach(o => {
            const day = (o.paid_at || o.created_at || '').slice(0, 10);
            if (day) byDate[day] = (byDate[day] || 0) + (o.commission_amount || 0);
        });
        const commissionSeries = Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b))
            .map(([date, commission]) => ({ date, commission }));
        return { totalCommission, totalRevenue, byType, commissionSeries };
    }, [orders]);

    // Data for charts
    const statusPieData = useMemo(() => {
        if (!stats?.by_status) return [];
        return Object.entries(stats.by_status)
            .filter(([, v]) => v > 0)
            .map(([k, v]) => ({ name: statusLabels[k]?.label || k, value: v }));
    }, [stats]);

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Mis ventas</h1>
                <Button variant="outline" size="sm" onClick={load} data-testid="sales-reload"><RefreshCcw className="h-4 w-4 mr-1" />Actualizar</Button>
            </div>

            {/* Statistics cards */}
            {stats && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <StatCard icon={Package} label="Total órdenes" value={stats.total_orders} tone="text-neutral-900 dark:text-white" testId="stat-total-orders" />
                    <StatCard icon={DollarSign} label="Ingresos realizados" value={formatMoney(stats.revenue_realized, currency)} tone="text-emerald-600" testId="stat-revenue-realized" />
                    <StatCard icon={ShieldCheck} label="En escrow" value={formatMoney(stats.revenue_in_escrow, currency)} tone="text-blue-600" testId="stat-revenue-escrow" />
                    <StatCard icon={TrendingUp} label="Ticket promedio" value={formatMoney(stats.avg_order_value, currency)} tone="text-red-600" testId="stat-avg-order" />
                    <StatCard icon={AlertTriangle} label="Reembolsos pendientes" value={stats.pending_refund_requests} tone={stats.pending_refund_requests > 0 ? 'text-amber-600' : 'text-neutral-500'} testId="stat-refund-pending" />
                    <StatCard icon={RefreshCcw} label="Total reembolsado" value={formatMoney(stats.refunded_amount, currency)} tone="text-red-600" testId="stat-refunded" />
                    <StatCard icon={Clock} label="Pendientes pago" value={stats.by_status?.pending_payment || 0} tone="text-amber-600" testId="stat-pending-payment" />
                    <StatCard icon={CheckCircle2} label="Completadas" value={stats.by_status?.released || 0} tone="text-emerald-700" testId="stat-completed" />
                    <StatCard icon={Percent} label="Comisiones cobradas" value={formatMoney(commissionStats.totalCommission, currency)} tone="text-orange-600" testId="stat-total-commission" />
                    <StatCard icon={FileText} label="Anuncios Prime activos" value={commissionStats.byType['prime'] || 0} tone="text-amber-600" testId="stat-prime-ads" />
                </div>
            )}

            <Tabs defaultValue="orders">
                <TabsList>
                    <TabsTrigger value="orders" data-testid="tab-orders">Órdenes</TabsTrigger>
                    <TabsTrigger value="analytics" data-testid="tab-analytics">Analíticas</TabsTrigger>
                    <TabsTrigger value="comisiones" data-testid="tab-commissions">Comisiones</TabsTrigger>
                </TabsList>

                <TabsContent value="orders" className="space-y-4 mt-4">
                    {/* Filters */}
                    <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                            placeholder="Buscar por producto o comprador..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="sm:max-w-xs"
                            data-testid="sales-search"
                        />
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger className="sm:w-56" data-testid="sales-filter-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los estados</SelectItem>
                                <SelectItem value="refund_pending">🔴 Reembolsos pendientes</SelectItem>
                                <SelectItem value="pending_payment">Pendiente de pago</SelectItem>
                                <SelectItem value="paid">Pagado (por enviar)</SelectItem>
                                <SelectItem value="delivered">Entregado</SelectItem>
                                <SelectItem value="released">Completado</SelectItem>
                                <SelectItem value="refunded">Reembolsado</SelectItem>
                                <SelectItem value="cancelled">Cancelado</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {loading ? (
                        <div className="text-sm text-muted-foreground">Cargando...</div>
                    ) : filtered.length === 0 ? (
                        <div className="rounded-2xl border border-dashed bg-card p-10 text-center">
                            <Package className="h-10 w-10 mx-auto text-muted-foreground" />
                            <div className="mt-3 text-sm text-muted-foreground">
                                {orders.length === 0 ? 'Aún no tienes ventas.' : 'No hay órdenes con los filtros aplicados.'}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="sales-list">
                            {filtered.map(o => {
                                const s = statusLabels[o.status] || { label: o.status, variant: 'outline' };
                                const rr = o.refund_request;
                                return (
                                    <div key={o.id} className="rounded-2xl border bg-card p-3 sm:p-4" data-testid={`sale-${o.id}`}>
                                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                                            <div className="h-20 w-20 rounded-lg overflow-hidden bg-[hsl(var(--ok-surface-2))] shrink-0">
                                                {o.ad_snapshot?.media?.[0]?.url ?
                                                    <img src={mediaUrl(o.ad_snapshot.media[0].url)} alt="" className="h-full w-full object-cover" /> :
                                                    <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground">Sin foto</div>}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Badge variant={s.variant}>{s.label}</Badge>
                                                    {rr?.status === 'pending' && <Badge variant="outline" className="border-amber-500 text-amber-700">Reembolso solicitado</Badge>}
                                                    {rr?.status === 'rejected' && <Badge variant="outline" className="border-neutral-400 text-neutral-600">Reembolso rechazado</Badge>}
                                                    <span className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString('es-MX')}</span>
                                                </div>
                                                <div className="font-medium mt-1 truncate">{o.ad_snapshot?.title}</div>
                                                <div className="text-xs text-muted-foreground">Comprador: @{o.buyer?.nickname}</div>
                                                <div className="money mt-1">{formatMoney(o.item_price, o.currency?.toUpperCase())} <span className="text-xs text-muted-foreground">(recibirás al liberar escrow)</span></div>
                                                {rr?.status === 'pending' && (
                                                    <div className="mt-2 text-xs bg-amber-50 border border-amber-300 rounded p-2 text-amber-900">
                                                        <div className="font-medium">Motivo del reembolso:</div>
                                                        <div>{rr.reason}</div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex flex-row sm:flex-col gap-2 items-stretch sm:items-end flex-wrap">
                                                {o.status === 'paid' && !rr && (
                                                    <>
                                                        {!o.shipping?.tracking_number && (
                                                            <Button size="sm" variant="outline" onClick={() => setShipOrder(o)} data-testid={`create-label-${o.id}`}>
                                                                <Truck className="h-4 w-4 mr-1" />Crear guía
                                                            </Button>
                                                        )}
                                                        <Button size="sm" onClick={() => markDelivered(o.id)} className="bg-red-600 hover:bg-red-700" data-testid={`mark-delivered-${o.id}`}>
                                                            <CheckCircle2 className="h-4 w-4 mr-1" />Marcar entregado
                                                        </Button>
                                                    </>
                                                )}
                                                {rr?.status === 'pending' && (
                                                    <>
                                                        <Button size="sm" onClick={() => approveRefund(o.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-refund-${o.id}`}>
                                                            <ThumbsUp className="h-4 w-4 mr-1" />Aprobar reembolso
                                                        </Button>
                                                        <Button size="sm" variant="outline" onClick={() => setRejectOrder(o)} data-testid={`reject-refund-${o.id}`}>
                                                            <ThumbsDown className="h-4 w-4 mr-1" />Rechazar
                                                        </Button>
                                                    </>
                                                )}
                                                {o.status === 'released' && (
                                                    <Button size="sm" variant="outline" onClick={() => setRateOrder(o)} data-testid={`rate-buyer-${o.id}`}>
                                                        <Star className="h-4 w-4 mr-1" />Calificar comprador
                                                    </Button>
                                                )}
                                                <Link to={`/messages?to=${o.buyer_id}&ad=${o.ad_id}`}>
                                                    <Button size="sm" variant="outline" className="w-full" data-testid={`contact-buyer-${o.id}`}>
                                                        <MessageCircle className="h-4 w-4 mr-1" />Contactar
                                                    </Button>
                                                </Link>
                                                <Link to={`/ad/${o.ad_id}`} target="_blank">
                                                    <Button size="sm" variant="ghost" className="w-full" data-testid={`view-ad-${o.id}`}>
                                                        <ExternalLink className="h-4 w-4 mr-1" />Ver anuncio
                                                    </Button>
                                                </Link>
                                            </div>
                                        </div>
                                        {['paid', 'delivered', 'released'].includes(o.status) && (
                                            <div className="mt-3 border-t pt-3">
                                                <OrderShipmentPanel order={o} currentUserId={user?.id} onUpdated={load} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="analytics" className="space-y-4 mt-4">
                    {!stats ? (
                        <div className="text-sm text-muted-foreground">Cargando analíticas…</div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Revenue trend */}
                            <div className="rounded-2xl border bg-card p-4" data-testid="chart-revenue">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="font-semibold">Ingresos últimos 30 días</div>
                                    <TrendingUp className="h-4 w-4 text-red-600" />
                                </div>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={stats.daily_series || []}>
                                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                                            <YAxis tick={{ fontSize: 10 }} />
                                            <Tooltip formatter={(v) => formatMoney(v, currency)} />
                                            <Line type="monotone" dataKey="revenue" stroke="#dc2626" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Orders per day */}
                            <div className="rounded-2xl border bg-card p-4" data-testid="chart-orders">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="font-semibold">Órdenes por día</div>
                                    <Package className="h-4 w-4 text-red-600" />
                                </div>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={stats.daily_series || []}>
                                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                                            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                            <Tooltip />
                                            <Bar dataKey="orders" fill="#111827" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Status distribution */}
                            <div className="rounded-2xl border bg-card p-4" data-testid="chart-status">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="font-semibold">Distribución por estado</div>
                                </div>
                                <div className="h-64">
                                    {statusPieData.length === 0 ? (
                                        <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sin datos</div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={statusPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                                                    {statusPieData.map((entry, idx) => (
                                                        <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </div>

                            {/* Top selling ads */}
                            <div className="rounded-2xl border bg-card p-4" data-testid="chart-top-ads">
                                <div className="font-semibold mb-2">Top anuncios vendidos</div>
                                {(stats.top_ads || []).length === 0 ? (
                                    <div className="text-sm text-muted-foreground py-8 text-center">Aún sin ventas.</div>
                                ) : (
                                    <div className="space-y-2">
                                        {stats.top_ads.map((a, i) => (
                                            <div key={a.ad_id} className="flex items-center gap-3 rounded-lg border bg-secondary/40 p-2">
                                                <div className="h-8 w-8 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-sm">{i + 1}</div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium truncate">{a.title || a.ad_id}</div>
                                                    <div className="text-xs text-muted-foreground">{a.orders} órdenes · {formatMoney(a.revenue, currency)}</div>
                                                </div>
                                                <Link to={`/ad/${a.ad_id}`} target="_blank">
                                                    <Button size="sm" variant="ghost"><ExternalLink className="h-4 w-4" /></Button>
                                                </Link>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="comisiones" className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Commission summary cards */}
                        <div className="rounded-2xl border bg-card p-5 flex flex-col gap-4">
                            <div className="flex items-center gap-2 font-semibold"><Percent className="h-4 w-4 text-orange-600" />Resumen de comisiones</div>
                            <div className="divide-y text-sm">
                                <div className="flex justify-between py-2.5">
                                    <span className="text-muted-foreground">Ingresos brutos (ventas completadas)</span>
                                    <span className="font-semibold tabular-nums">{formatMoney(commissionStats.totalRevenue, currency)}</span>
                                </div>
                                <div className="flex justify-between py-2.5">
                                    <span className="text-muted-foreground">Comisiones cobradas por la plataforma</span>
                                    <span className="font-semibold tabular-nums text-orange-600">− {formatMoney(commissionStats.totalCommission, currency)}</span>
                                </div>
                                <div className="flex justify-between py-2.5 bg-emerald-50/60 px-2 rounded-lg">
                                    <span className="font-semibold text-emerald-700">Ingresos netos estimados</span>
                                    <span className="font-bold tabular-nums text-emerald-700">{formatMoney(Math.max(0, commissionStats.totalRevenue - commissionStats.totalCommission), currency)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Commission by listing type */}
                        <div className="rounded-2xl border bg-card p-5 flex flex-col gap-4">
                            <div className="flex items-center gap-2 font-semibold"><FileText className="h-4 w-4 text-orange-600" />Ventas por tipo de publicación</div>
                            <div className="space-y-2">
                                {Object.entries(commissionStats.byType).length === 0 ? (
                                    <div className="text-sm text-muted-foreground py-6 text-center">Sin ventas completadas aún.</div>
                                ) : Object.entries(commissionStats.byType).map(([type, count]) => (
                                    <div key={type} className="flex items-center justify-between rounded-lg border px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <span className={`inline-block w-2 h-2 rounded-full ${type === 'prime' ? 'bg-amber-500' : type === 'gratuita' ? 'bg-emerald-500' : 'bg-neutral-400'}`} />
                                            <span className="text-sm font-medium capitalize">{type === 'prime' ? 'Prime' : type === 'gratuita' ? 'Gratuita' : 'Legado'}</span>
                                        </div>
                                        <span className="text-sm font-semibold tabular-nums">{count} venta{count !== 1 ? 's' : ''}</span>
                                    </div>
                                ))}
                                <p className="text-xs text-muted-foreground mt-2">
                                    Publicaciones Prime generan comisión según la categoría. Publicaciones Gratuitas tienen comisión 0%.
                                </p>
                            </div>
                        </div>

                        {/* Commission over time chart */}
                        {commissionStats.commissionSeries.length > 0 && (
                            <div className="rounded-2xl border bg-card p-4 lg:col-span-2">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="font-semibold text-sm">Comisiones en el tiempo</div>
                                    <Percent className="h-4 w-4 text-orange-600" />
                                </div>
                                <div className="h-56">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={commissionStats.commissionSeries}>
                                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                                            <YAxis tick={{ fontSize: 10 }} />
                                            <Tooltip formatter={(v) => formatMoney(v, currency)} />
                                            <Bar dataKey="commission" fill="#f97316" name="Comisión" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}

                        {/* Link to full commission page */}
                        <div className="rounded-2xl border border-dashed bg-muted/40 p-4 flex flex-col items-center justify-center gap-2 text-center lg:col-span-2">
                            <Percent className="h-6 w-6 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">Para más detalles sobre comisiones, cotizador interactivo y tabla por categoría, visita</p>
                            <a href="/dashboard/comisiones" className="text-sm font-semibold text-red-600 underline underline-offset-2">Mi sección de Comisiones →</a>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Reject refund dialog */}
            <Dialog open={!!rejectOrder} onOpenChange={() => { setRejectOrder(null); setRejectReason(''); }}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Rechazar solicitud de reembolso</DialogTitle>
                        <DialogDescription>Explica al comprador por qué rechazas su solicitud.</DialogDescription>
                    </DialogHeader>
                    <Textarea
                        placeholder="Motivo del rechazo (mín 3 caracteres)..."
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={4}
                        data-testid="reject-reason"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setRejectOrder(null); setRejectReason(''); }}>Cancelar</Button>
                        <Button variant="destructive" onClick={doRejectRefund} data-testid="submit-reject-refund">Rechazar reembolso</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rate buyer dialog */}
            <Dialog open={!!rateOrder} onOpenChange={() => setRateOrder(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Calificar comprador</DialogTitle>
                        <DialogDescription>Comparte tu experiencia con @{rateOrder?.buyer?.nickname}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map(n => (
                                <button key={n} onClick={() => setStars(n)} className={`h-10 w-10 text-2xl transition-transform hover:scale-110 ${n <= stars ? 'text-amber-500' : 'text-neutral-300'}`} data-testid={`sale-rate-star-${n}`}>★</button>
                            ))}
                            <span className="text-sm text-muted-foreground ml-2">{stars}/5</span>
                        </div>
                        <Textarea placeholder="Comentario (opcional)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="sale-rate-comment" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRateOrder(null)}>Cancelar</Button>
                        <Button onClick={submitRating} className="bg-red-600 hover:bg-red-700" data-testid="submit-sale-rating">Enviar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ShippingLabelModal open={!!shipOrder} onOpenChange={() => setShipOrder(null)} order={shipOrder} onDone={() => load()} />
        </div>
    );
}
